import { useEffect, useCallback } from 'react'

interface KeyboardShortcutOptions {
  ctrl?: boolean
  meta?: boolean
  shift?: boolean
  alt?: boolean
  preventDefault?: boolean
}

/**
 * Hook for registering keyboard shortcuts
 *
 * @param key - The key to listen for (e.g., 'k', 'Escape', 'Enter')
 * @param callback - Function to call when shortcut is triggered
 * @param options - Modifier key requirements
 */
export function useKeyboardShortcut(
  key: string,
  callback: () => void,
  options: KeyboardShortcutOptions = {}
) {
  const {
    ctrl = false,
    meta = false,
    shift = false,
    alt = false,
    preventDefault = true,
  } = options

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Check if focus is in an input, textarea, or contenteditable element
      const target = event.target as HTMLElement
      const isInputFocused =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable

      // For Escape key and Ctrl+K, allow them to work even in inputs
      // These are global shortcuts that should work from anywhere
      const isGlobalShortcut = key === 'Escape' || (key.toLowerCase() === 'k' && (ctrl || meta))

      if (!isGlobalShortcut && isInputFocused) {
        return
      }

      // Check modifier keys
      const ctrlPressed = event.ctrlKey || event.metaKey // Support Cmd on Mac
      const shiftPressed = event.shiftKey
      const altPressed = event.altKey

      // For shortcuts that require ctrl/meta, accept either
      const modifiersMatch =
        (ctrl || meta ? ctrlPressed : !ctrlPressed) &&
        (shift ? shiftPressed : !shiftPressed) &&
        (alt ? altPressed : !altPressed)

      // Check if the key matches
      const keyMatches = event.key.toLowerCase() === key.toLowerCase()

      if (keyMatches && modifiersMatch) {
        if (preventDefault) {
          event.preventDefault()
        }
        callback()
      }
    },
    [key, callback, ctrl, meta, shift, alt, preventDefault]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleKeyDown])
}

/**
 * Hook for Escape key - commonly used to close modals/overlays
 */
export function useEscapeKey(callback: () => void) {
  useKeyboardShortcut('Escape', callback, { preventDefault: false })
}

export default useKeyboardShortcut
